import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, useApi } from "../lib/api";
import { Badge, Button, Card, Empty, ErrorNote, fmtDate, inputCls, Loading, Modal, useAction } from "../components/ui";

const ICON: Record<string, string> = { sent: "✉", reply: "↩", bounce: "⚠", ooo: "🏖", task_done: "✓", paused: "⏸", resumed: "▶", stopped: "■", approved: "✔", rejected: "✖", drafted: "✎", enrolled: "+", edited: "✎", new_opp: "$", account_pause: "⏸", planned: "◷" };

export default function ContactDetail() {
  const { id } = useParams();
  const { data, error, loading, reload } = useApi<any>(`/contacts/${id}`, [id]);
  const [dnc, setDnc] = useState(false);
  const [reason, setReason] = useState("");
  const act = useAction();
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote error={error} />;
  const c = data.contact;
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-[1.45rem] font-semibold tracking-tight">{c.firstName} {c.lastName}</h1>
        <div className="text-sm text-muted">{c.title} · <Link className="text-accent-text hover:underline" to={`/accounts/${data.account?.id}`}>{data.account?.name}</Link> · {c.state}</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {c.aiSdrStatus ? <Badge>{c.aiSdrStatus}</Badge> : <Badge tone="skipped">not enrolled</Badge>}
          {c.emailOptOut && <Badge tone="Do Not Contact">Email opt-out</Badge>}
          {c.doNotCall && <Badge tone="Paused">No calls</Badge>}
          {c.emailBounced && <Badge tone="Bounced">Bounced</Badge>}
        </div>
      </div>
      {!c.emailOptOut && (
        <div>
          <Button tone="ghost" onClick={() => setDnc(true)}>Do not contact…</Button>
        </div>
      )}
      {dnc && (
        <Modal title={`Do not contact ${c.firstName} ${c.lastName}?`} onClose={() => setDnc(false)} actions={<><Button onClick={() => setDnc(false)}>Cancel</Button><Button tone="danger" disabled={act.busy} onClick={() => act.run(async () => { await api(`/contacts/${c.id}/do-not-contact`, { body: { reason } }); setDnc(false); reload(); })}>Do not contact</Button></>}>
          <p>Their email addresses go on the suppression list, so no campaign or re-import can contact them again. Any running sequence stops.</p>
          <textarea className={inputCls} rows={2} placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <ErrorNote error={act.error} />
        </Modal>
      )}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card title="Timeline">
          {!data.timeline.length ? (
            <Empty>No touches yet.</Empty>
          ) : (
            <ol className="relative space-y-3 border-l border-line pl-5">
              {data.timeline.map((t: any, i: number) => (
                <li key={i} className="text-sm">
                  <span className="absolute -left-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-surface text-xs ring-1 ring-line">{ICON[t.type] ?? "•"}</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <b className="capitalize">{t.type.replace("_", " ")}</b>
                    <span className="text-muted">{t.detail}</span>
                  </div>
                  <div className="text-xs text-muted">
                    {fmtDate(t.at)} · {t.campaignName} ·{" "}
                    <Link className="text-accent-text hover:underline" to={`/campaigns/${t.campaignId}/contacts/${t.contactId}`}>open sequence</Link>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
        <div className="space-y-4">
          <Card title="Contact">
            <dl className="space-y-1 text-sm">
              {[
                ["Email", c.email],
                ["Phone", c.phone ?? c.mobile],
                ["Level", c.managementLevel],
                ["Department", c.department],
                ["Buyer role", c.buyerRole],
                ["Outreach sequence", c.outreachSequence],
                ["Territory owner", data.territoryOwner],
                ["Intellias relationship", "unknown (Infor data)"],
                ["Last AI touch", fmtDate(c.lastAiTouch)],
              ].map(([k, v]) => (
                <div key={String(k)} className="flex justify-between gap-2"><dt className="text-muted">{k}</dt><dd className="truncate text-right">{v ?? "—"}</dd></div>
              ))}
            </dl>
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              {c.linkedinUrl && <a className="text-accent-text hover:underline" href={c.linkedinUrl} target="_blank" rel="noreferrer">LinkedIn</a>}
              {data.salesforceUrl ? <a className="text-accent-text hover:underline" href={data.salesforceUrl} target="_blank" rel="noreferrer">Salesforce record</a> : <span className="text-xs text-muted">Salesforce link available once the integration is connected (Phase 4)</span>}
            </div>
          </Card>
          <Card title="Enrollments">
            {data.enrollments.map((e: any) => (
              <div key={e.campaignId} className="flex items-center justify-between py-1 text-sm">
                <Link className="text-accent-text hover:underline" to={`/campaigns/${e.campaignId}/contacts/${c.id}`}>{e.offering}</Link>
                <Badge>{e.status}</Badge>
              </div>
            ))}
            {!data.enrollments.length && <Empty>None</Empty>}
          </Card>
        </div>
      </div>
    </div>
  );
}
